import React, { useState, useEffect } from 'react';
import { RecentTradesList } from './RecentTradesList';
import { tradingApi } from '../utils/api';

interface TradingChartProps {
  tokenAddress?: string;
  refreshInterval?: number;
}

interface TimeFrameStats {
  priceChange: number;
  volume: number;
  netInflow: number;
}

export const TradingChart: React.FC<TradingChartProps> = ({ refreshInterval = 30000 }) => {
  const [currentPrice, setCurrentPrice] = useState<number>(0);
  const [stats24h, setStats24h] = useState<TimeFrameStats>({ priceChange: 0, volume: 0, netInflow: 0 });
  const [stats7d, setStats7d] = useState<TimeFrameStats>({ priceChange: 0, volume: 0, netInflow: 0 });
  const [stats30d, setStats30d] = useState<TimeFrameStats>({ priceChange: 0, volume: 0, netInflow: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch trades and calculate all stats from them
  useEffect(() => {
    let isInitialLoad = true;

    const fetchStats = async () => {
      try {
        // Only show loading state on initial load, not on refreshes
        if (isInitialLoad) {
          setIsLoading(true);
        }

        // Fetch trades from last 30 days - this is the ONLY data source we need
        const to = Date.now();
        const from30d = to - (30 * 24 * 60 * 60 * 1000);
        const txResponse = await tradingApi.getDexTransactionsByTimestamp(from30d, to, 1000);

        let responseData = txResponse;
        if (txResponse && typeof txResponse === 'object' && 'data' in txResponse) {
          responseData = (txResponse as any).data;
        }

        // Validate we have trades data
        if (!responseData || !responseData.transactions || responseData.transactions.length === 0) {
          throw new Error('No trading data available - please check if BWS token has recent activity');
        }

        const allTrades = responseData.transactions;
        const ethPriceUsd = responseData.ethPriceUsd || 3800; // Use API ETH price, fallback if missing

        // Sort trades by timestamp (oldest first for calculations)
        const sortedTrades = [...allTrades].sort((a, b) => {
          const timeA = new Date(a.timestamp).getTime();
          const timeB = new Date(b.timestamp).getTime();
          return timeA - timeB;
        });

        // Get current price from most recent trade's priceUsd field
        const mostRecentTrade = sortedTrades[sortedTrades.length - 1];
        const currentPriceValue = parseFloat(mostRecentTrade.priceUsd || '0');

        if (currentPriceValue === 0) {
          throw new Error('Invalid price data from recent trades');
        }

        setCurrentPrice(currentPriceValue);
        console.log('Current BWS price from most recent trade:', {
          priceUsd: currentPriceValue,
          ethPriceUsd,
          timestamp: new Date(mostRecentTrade.timestamp).toISOString(),
          tradeType: mostRecentTrade.type
        });

        // Helper function to calculate stats for a time range
        const calculateStats = (hoursAgo: number): TimeFrameStats => {
          const cutoffTime = to - (hoursAgo * 60 * 60 * 1000);
          const tradesInRange = sortedTrades.filter((trade: any) => {
            const tradeTime = new Date(trade.timestamp).getTime();
            return tradeTime >= cutoffTime;
          });

          if (tradesInRange.length === 0) {
            return { priceChange: 0, volume: 0, netInflow: 0 };
          }

          // Get oldest and newest trades in this specific range for price change
          const oldestInRange = tradesInRange[0];
          const newestInRange = tradesInRange[tradesInRange.length - 1];
          const oldPrice = parseFloat(oldestInRange.priceUsd || '0');
          const newPrice = parseFloat(newestInRange.priceUsd || '0');
          const priceChange = oldPrice > 0
            ? ((newPrice - oldPrice) / oldPrice) * 100
            : 0;

          // Calculate volume (sum of all trade values in USD)
          const volume = tradesInRange.reduce((sum: number, trade: any) => {
            const tradeValue = parseFloat(trade.totalUsd || '0');
            return sum + tradeValue;
          }, 0);

          // Calculate net inflow (buys - sells in USD)
          // Positive = more buying pressure (bullish), Negative = more selling pressure (bearish)
          const netInflow = tradesInRange.reduce((sum: number, trade: any) => {
            const tradeValue = parseFloat(trade.totalUsd || '0');
            return trade.type === 'buy' ? sum + tradeValue : sum - tradeValue;
          }, 0);

          return { priceChange, volume, netInflow };
        };

        // Calculate stats for all time frames
        const calculated24h = calculateStats(24);
        const calculated7d = calculateStats(168);
        const calculated30d = calculateStats(720);

        setStats24h(calculated24h);
        setStats7d(calculated7d);
        setStats30d(calculated30d);

        console.log('Trading Stats:', {
          totalTrades: allTrades.length,
          ethPriceUsd,
          currentPrice: currentPriceValue,
          stats24h: calculated24h,
          stats7d: calculated7d,
          stats30d: calculated30d
        });

        setError(null);

        if (isInitialLoad) {
          setIsLoading(false);
          isInitialLoad = false;
        }
      } catch (err) {
        console.error('Failed to fetch stats:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch data');
        if (isInitialLoad) {
          setIsLoading(false);
          isInitialLoad = false;
        }
      }
    };

    fetchStats();
    const interval = setInterval(fetchStats, refreshInterval);
    return () => clearInterval(interval);
  }, [refreshInterval]);

  const formatPrice = (price: number) => {
    if (!price || isNaN(price) || price === 0) return '-';
    if (price < 0.0001) return `$${price.toFixed(8)}`; // Very small prices (like old ETH prices)
    if (price < 0.01) return `$${price.toFixed(6)}`;
    if (price < 1) return `$${price.toFixed(4)}`;
    return `$${price.toFixed(2)}`;
  };

  const formatVolume = (volume: number) => {
    if (volume >= 1000000) return `${(volume / 1000000).toFixed(2)}M`;
    if (volume >= 1000) return `${(volume / 1000).toFixed(2)}K`;
    return volume.toFixed(0);
  };

  if (isLoading) {
    return (
      <div className="card shadow-sm">
        <div className="card-body">
          <div className="placeholder-glow">
            <span className="placeholder col-3 mb-3"></span>
            <span className="placeholder col-12 mb-2"></span>
            <span className="placeholder col-12"></span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card shadow-sm">
        <div className="card-body">
          <div className="mb-4">
            <h2 className="h5 fw-semibold mb-1">BWS/ETH Trading Activity</h2>
            <p className="small text-muted">Real-time on-chain data from Uniswap V2</p>
          </div>
          <div className="alert alert-warning mb-0">
            <p className="small mb-2">{error}</p>
            <p className="small text-muted mb-0">
              Trading data will appear once BWS activity is detected.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card shadow-sm">
      {/* Header */}
      <div className="card-header bg-transparent border-bottom">
        <div className="mb-3">
          <h2 className="h5 fw-semibold mb-1">BWS/ETH Trading Activity</h2>
          <p className="small text-muted mb-0">Real-time on-chain data from Uniswap V2</p>
        </div>

        {/* Current Price */}
        <div className="row g-3 mb-3">
          <div className="col-12">
            <div className="small text-muted">Current Price</div>
            <div className="h3 fw-bold mb-0">{formatPrice(currentPrice)}</div>
          </div>
        </div>

        {/* Price Stats - All Time Frames */}
        <div className="row g-3">
          {/* 24h Stats */}
          <div className="col-12 col-lg-4">
            <div className="p-3 border rounded">
              <div className="small fw-semibold text-muted mb-2">24 Hours</div>
              <div className="row g-2">
                <div className="col-12">
                  <div className="small text-muted">Change</div>
                  <div className={`fw-bold d-flex align-items-center gap-1 ${
                    stats24h.priceChange >= 0 ? 'text-success' : 'text-danger'
                  }`}>
                    {!isNaN(stats24h.priceChange) && stats24h.priceChange !== 0 && (
                      stats24h.priceChange >= 0 ? <i className="bi bi-arrow-up-right"></i> : <i className="bi bi-arrow-down-right"></i>
                    )}
                    {!isNaN(stats24h.priceChange) ? `${stats24h.priceChange >= 0 ? '+' : ''}${stats24h.priceChange.toFixed(2)}%` : '-'}
                  </div>
                </div>
                <div className="col-6">
                  <div className="small text-muted">Volume</div>
                  <div className="fw-semibold">${formatVolume(stats24h.volume)}</div>
                </div>
                <div className="col-6">
                  <div className="small text-muted">Net Inflow</div>
                  <div className={`fw-semibold ${
                    stats24h.netInflow >= 0 ? 'text-success' : 'text-danger'
                  }`}>
                    ${stats24h.netInflow >= 0 ? '+' : '-'}{formatVolume(Math.abs(stats24h.netInflow))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 7d Stats */}
          <div className="col-12 col-lg-4">
            <div className="p-3 border rounded">
              <div className="small fw-semibold text-muted mb-2">7 Days</div>
              <div className="row g-2">
                <div className="col-12">
                  <div className="small text-muted">Change</div>
                  <div className={`fw-bold d-flex align-items-center gap-1 ${
                    stats7d.priceChange >= 0 ? 'text-success' : 'text-danger'
                  }`}>
                    {!isNaN(stats7d.priceChange) && stats7d.priceChange !== 0 && (
                      stats7d.priceChange >= 0 ? <i className="bi bi-arrow-up-right"></i> : <i className="bi bi-arrow-down-right"></i>
                    )}
                    {!isNaN(stats7d.priceChange) ? `${stats7d.priceChange >= 0 ? '+' : ''}${stats7d.priceChange.toFixed(2)}%` : '-'}
                  </div>
                </div>
                <div className="col-6">
                  <div className="small text-muted">Volume</div>
                  <div className="fw-semibold">${formatVolume(stats7d.volume)}</div>
                </div>
                <div className="col-6">
                  <div className="small text-muted">Net Inflow</div>
                  <div className={`fw-semibold ${
                    stats7d.netInflow >= 0 ? 'text-success' : 'text-danger'
                  }`}>
                    ${stats7d.netInflow >= 0 ? '+' : '-'}{formatVolume(Math.abs(stats7d.netInflow))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 30d Stats */}
          <div className="col-12 col-lg-4">
            <div className="p-3 border rounded">
              <div className="small fw-semibold text-muted mb-2">30 Days</div>
              <div className="row g-2">
                <div className="col-12">
                  <div className="small text-muted">Change</div>
                  <div className={`fw-bold d-flex align-items-center gap-1 ${
                    stats30d.priceChange >= 0 ? 'text-success' : 'text-danger'
                  }`}>
                    {!isNaN(stats30d.priceChange) && stats30d.priceChange !== 0 && (
                      stats30d.priceChange >= 0 ? <i className="bi bi-arrow-up-right"></i> : <i className="bi bi-arrow-down-right"></i>
                    )}
                    {!isNaN(stats30d.priceChange) ? `${stats30d.priceChange >= 0 ? '+' : ''}${stats30d.priceChange.toFixed(2)}%` : '-'}
                  </div>
                </div>
                <div className="col-6">
                  <div className="small text-muted">Volume</div>
                  <div className="fw-semibold">${formatVolume(stats30d.volume)}</div>
                </div>
                <div className="col-6">
                  <div className="small text-muted">Net Inflow</div>
                  <div className={`fw-semibold ${
                    stats30d.netInflow >= 0 ? 'text-success' : 'text-danger'
                  }`}>
                    ${stats30d.netInflow >= 0 ? '+' : '-'}{formatVolume(Math.abs(stats30d.netInflow))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Trades */}
      <RecentTradesList
        refreshInterval={refreshInterval}
      />
    </div>
  );
};

export default TradingChart;
